import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { MatDialog } from '@angular/material/dialog';
import { usersService } from 'src/app/services/services/users.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { NGXLogger } from 'ngx-logger';
import { finalize } from 'rxjs';
import { SelectResourceDialogComponent } from './select-resource-dialog.component';
import { Store } from '@ngrx/store';
import * as mainActions from 'src/app/state/app.actions';
import { InfoMessage } from 'src/app/common/messages';
import { mainState } from 'src/app/state/app.reducer';

export interface ResourceTypeContext {
  resourceTypeId: number;
  resourceDescription: string;
  contextList: ContextEntity[];
  isExpanded?: boolean;
  hasChanges?: boolean;
  snapshot?: any;
}

export interface ContextEntity {
  contextId: number;
  contextDescription: string;
  selected: boolean;
}

export interface FiltersetResource {
  resourceId: string;
  description: string;
  resourceType?: number;
}

@Component({
    selector: 'app-users-filter-get-filter',
    templateUrl: './getFilter.component.html',
    styleUrls: ['./getFilter.component.scss'],
    standalone: false
})
export class GetFilterComponent implements OnInit, OnDestroy {
  filtersetId: number = 0;
  filtersetName: string = '';
  resourceTypeContextList: ResourceTypeContext[] = [];
  isLoading = true;

  // Resource management per resource type
  resourceItemsUsed: { [key: number]: FiltersetResource[] } = {};
  resourceItemsAvailable: { [key: number]: FiltersetResource[] } = {};
  activeCheckboxes: { [key: number]: number[] } = {};

  // Accordion state
  openAccordions = 0;
  collapseExpandText = this.i18n.transform('users:properties_lang_expandAll');
  filter = '';

  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label:  this.i18n.transform('users:users_tooltip_filterHeader'), url: '/users/users-filter' },
    { label: this.i18n.transform('users:usersgetfilter_header') , url: '' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private i18n: I18NextPipe,
    private dialog: MatDialog,
    private usersService: usersService,
    private logger: NGXLogger,
    private store: Store<mainState>
  ) { }

  ngOnInit(): void {
    const filterIdParam = this.route.snapshot.paramMap.get('filterId');
    if (filterIdParam) {
      this.filtersetId = parseInt(filterIdParam, 10);
      this.loadFilterData();
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private loadFilterData(): void {
    this.isLoading = true;
    this.usersService.getFilter(this.filtersetId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result) => {
          if (result) {
            this.filtersetName = result.filtersetName || '';
            this.BreadcrumbsLink[2].label = this.filtersetName;
            this.resourceTypeContextList = (result.resourceTypeContextList || []).map((rt: any) => ({
              resourceTypeId: rt.resourceTypeId,
              resourceDescription: rt.resourceDescription,
              contextList: rt.contextList || [],
              isExpanded: false,
              hasChanges: false,
              snapshot: null
            }));

            this.resourceTypeContextList.sort((a, b) => this.getResourceTypeName(a.resourceTypeId).localeCompare(this.getResourceTypeName(b.resourceTypeId)));

            // Initialize active checkboxes from contextList
            this.resourceTypeContextList.forEach(rt => {
              this.activeCheckboxes[rt.resourceTypeId] = rt.contextList
                .filter(c => c.selected)
                .map(c => c.contextId);
            });

            this.updateCollapseExpandText();
          }
        },
        error: (error) => {
          this.logger.error('Error loading filter:', error);
        }
      });
  }

  toggleAccordion(rt: ResourceTypeContext): void {
    rt.isExpanded = !rt.isExpanded;

    if (rt.isExpanded) {
      this.openAccordions++;
      if (!rt.snapshot) {
        this.loadResourcesForType(rt.resourceTypeId);
      }
    } else {
      this.openAccordions--;
    }

    this.updateCollapseExpandText();
  }

  collapseExpandAll(): void {
    const shouldExpand = this.openAccordions < this.resourceTypeContextList.length / 2;

    this.resourceTypeContextList.forEach(rt => {
      if (rt.isExpanded !== shouldExpand) {
        rt.isExpanded = shouldExpand;
        if (shouldExpand && !rt.snapshot) {
          this.loadResourcesForType(rt.resourceTypeId);
        }
      }
    });

    this.openAccordions = shouldExpand ? this.resourceTypeContextList.length : 0;
    this.updateCollapseExpandText();
  }

  private updateCollapseExpandText(): void {
    const total = this.resourceTypeContextList.length;
    if (this.openAccordions >= total / 2) {
      this.collapseExpandText = this.i18n.transform('users:properties_lang_collapseAll');
    } else {
      this.collapseExpandText = this.i18n.transform('users:properties_lang_expandAll');
    }
  }

  private loadResourcesForType(resourceTypeId: number): void {
    this.usersService.getFiltersetResourceList(this.filtersetId, resourceTypeId)
      .subscribe({
        next: (result) => {
          if (result) {
            this.resourceItemsUsed[resourceTypeId] = (result || []).sort((a: FiltersetResource, b: FiltersetResource) =>
              (a.description || '').localeCompare(b.description || '')
            );
            this.loadAvailableResources(resourceTypeId);
            this.takeSnapshot(resourceTypeId);
          }
        },
        error: (error) => {
          this.logger.error(`Error loading resources for type ${resourceTypeId}:`, error);
        }
      });
  }

  private loadAvailableResources(resourceTypeId: number): void {
    this.usersService.getAvailableResourcesPrType(this.filtersetId, resourceTypeId)
      .subscribe({
        next: (result) => {
          if (result) {
            this.resourceItemsAvailable[resourceTypeId] = (result || []).sort((a: FiltersetResource, b: FiltersetResource) =>
              (a.description || '').localeCompare(b.description || '')
            );
          }
        },
        error: (error) => {
          this.logger.error(`Error loading available resources for type ${resourceTypeId}:`, error);
        }
      });
  }

  private takeSnapshot(resourceTypeId: number): void {
    const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
    if (rt) {
      rt.snapshot = {
        resources: JSON.parse(JSON.stringify(this.resourceItemsUsed[resourceTypeId] || [])),
        activeCheckboxes: JSON.parse(JSON.stringify(this.activeCheckboxes[resourceTypeId] || []))
      };
    }
  }

  addResource(resourceTypeId: number): void {
    const available = this.resourceItemsAvailable[resourceTypeId] || [];
    if (available.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(SelectResourceDialogComponent, {
      width: '762px',
      disableClose: false,
      data: {
        title: this.i18n.transform('users:usersfilter_Add') + ' ' +
               this.getResourceTypeName(resourceTypeId),
        listHeaderText: this.i18n.transform('users:usersfilter_selectlist_listHeaderText') + ' ' + this.getResourceTypeName(resourceTypeId) + ':',
        availableResources: available,
        onResourceSelected: (resource: FiltersetResource) => {
          // Remove from available (already removed in dialog)
          const availableList = this.resourceItemsAvailable[resourceTypeId] || [];
          const index = availableList.findIndex(r => r.resourceId === resource.resourceId);
          if (index >= 0) {
            availableList.splice(index, 1);
          }

          // Add to used
          const usedList = this.resourceItemsUsed[resourceTypeId] || [];
          if (!usedList.some(r => r.resourceId === resource.resourceId)) {
            usedList.push(resource);
            usedList.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
          }

          this.updateChangesState(resourceTypeId);
        }
      }
    });
  }

  removeResource(resourceTypeId: number, resource: FiltersetResource): void {
    const used = this.resourceItemsUsed[resourceTypeId] || [];
    const index = used.findIndex(r => r.resourceId === resource.resourceId);
    if (index >= 0) {
      used.splice(index, 1);
      // Add back to available
      const available = this.resourceItemsAvailable[resourceTypeId] || [];
      available.push(resource);
      available.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
      this.updateChangesState(resourceTypeId);
    }
  }

  onContextCheckboxChange(resourceTypeId: number, contextId: number, checked: boolean): void {
    if (!this.activeCheckboxes[resourceTypeId]) {
      this.activeCheckboxes[resourceTypeId] = [];
    }

    if (checked) {
      if (!this.activeCheckboxes[resourceTypeId].includes(contextId)) {
        this.activeCheckboxes[resourceTypeId].push(contextId);
      }
    } else {
      const index = this.activeCheckboxes[resourceTypeId].indexOf(contextId);
      if (index >= 0) {
        this.activeCheckboxes[resourceTypeId].splice(index, 1);
      }
    }

    this.updateChangesState(resourceTypeId);
  }

  isContextChecked(resourceTypeId: number, contextId: number): boolean {
    return (this.activeCheckboxes[resourceTypeId] || []).includes(contextId);
  }

  saveChanges(resourceTypeId: number): void {
    const diff = this.calculateDiff(resourceTypeId);
    const changeCount = diff.resourceAdds.length + diff.resourceDeletes.length +
                       diff.contextAdds.length + diff.contextDeletes.length;

    if (changeCount === 0) {
      return;
    }

    this.isLoading = true;
    this.usersService.updateFilterset(this.filtersetId, resourceTypeId, diff)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result) => {
          if (result && result.errorCount === 0) {
            // Immediately update state to disable save button
            const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
            if (rt) {
              rt.hasChanges = false;
            }
            // Show success message
            this.store.dispatch(
              mainActions.addMessage({
                message: new InfoMessage(this.i18n.transform('users:shared_err_ok'), '')
              })
            );
            // Success - reload resources
            this.loadResourcesForType(resourceTypeId);
          } else {
            this.logger.error('Error saving filterset:', result);
          }
        },
        error: (error) => {
          this.logger.error('Error saving filterset:', error);
        }
      });
  }

  undoChanges(resourceTypeId: number): void {
    const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
    if (rt && rt.snapshot) {
      this.resourceItemsUsed[resourceTypeId] = JSON.parse(JSON.stringify(rt.snapshot.resources));
      this.activeCheckboxes[resourceTypeId] = JSON.parse(JSON.stringify(rt.snapshot.activeCheckboxes));
      this.loadAvailableResources(resourceTypeId);
      this.updateChangesState(resourceTypeId);
    }
  }

  private calculateDiff(resourceTypeId: number): any {
    const currentResources = (this.resourceItemsUsed[resourceTypeId] || []).map(r => r.resourceId);
    const snapshotResources = (this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId)?.snapshot?.resources || [])
      .map((r: FiltersetResource) => r.resourceId);

    const resourceAdds = currentResources.filter(id => !snapshotResources.includes(id));
    const resourceDeletes = snapshotResources.filter((id: string) => !currentResources.includes(id));

    const currentContexts = this.activeCheckboxes[resourceTypeId] || [];
    const snapshotContexts = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId)?.snapshot?.activeCheckboxes || [];

    const contextAdds = currentContexts.filter(id => !snapshotContexts.includes(id)).map(id => id.toString());
    const contextDeletes = snapshotContexts.filter((id: number) => !currentContexts.includes(id)).map(id => id.toString());

    return {
      resourceAdds,
      resourceDeletes,
      contextAdds,
      contextDeletes
    };
  }

  private updateChangesState(resourceTypeId: number): void {
    const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
    if (rt) {
      const diff = this.calculateDiff(resourceTypeId);
      rt.hasChanges = diff.resourceAdds.length > 0 || diff.resourceDeletes.length > 0 ||
                     diff.contextAdds.length > 0 || diff.contextDeletes.length > 0;
    }
  }

  hasChanges(resourceTypeId: number): boolean {
    const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
    return rt?.hasChanges || false;
  }

  getResourceTypeName(resourceTypeId: number): string {
    return this.i18n.transform(`users:usersfilter_ResourceType_${resourceTypeId}`,
      { defaultValue: `Resource Type ${resourceTypeId}` });
  }

  getActiveStatusFlag(resourceTypeId: number): boolean {
    const rt = this.resourceTypeContextList.find(r => r.resourceTypeId === resourceTypeId);
    if (rt && rt.contextList) {
      const hasActive = rt.contextList.some(c => this.isContextChecked(resourceTypeId, c.contextId));
      return hasActive ? true : false;
    }
    return false;
  }

  applyFilter(): void {}

  getFilteredResources(resourceTypeId: number): FiltersetResource[] {
    const resources = this.resourceItemsUsed[resourceTypeId] || [];
    if (!this.filter || this.filter.trim() === '') {
      return resources;
    }
    const filterLower = this.filter.toLowerCase().trim();
    return resources.filter(resource =>
      (resource.description || '').toLowerCase().includes(filterLower)
    );
  }

  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('users:help_title_getfilter'),
        text: this.i18n.transform('users:help_getfilter'),
        renderAsHtml: true
      }
    });
  }
}
